import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Icon from '../components/atoms/Icon';
import Badge from '../components/atoms/Badge';
import Avatar from '../components/atoms/Avatar';
import { getApplications, getJobDetails } from '../services/api';
import { getMessages, sendMessage, markMessagesAsRead, getUnreadCount, initializeConversation } from '../services/messaging';
import SessionManager from '../utils/sessionManager';

const MyApplicationsPage = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [candidateNotifications, setCandidateNotifications] = useState([]);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Try to migrate legacy session first
    SessionManager.migrateLegacySession();
    
    // Check if user is logged in as candidate using new session system
    const isLoggedInAsCandidate = SessionManager.isLoggedIn('candidate');
    const currentSession = SessionManager.getCurrentSession();
    
    // If not logged in as candidate, redirect to login page
    if (!isLoggedInAsCandidate || (currentSession && currentSession.role !== 'candidate')) {
      navigate('/login');
      return;
    }

    loadApplications();
  }, [navigate]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      
      // Get candidate session data using new session system
      const candidateSession = SessionManager.getSession('candidate');
      const candidateId = candidateSession?.user_id;
      const email = candidateSession?.email;
      
      const filters = {};
      if (candidateId) {
        filters.candidate_id = candidateId;
      } else if (email) {
        filters.candidate_email = email;
      }
      
      const data = await getApplications(filters);
      setApplications(data);
      setCandidateNotifications(buildCandidateNotifications(data));
      
      // Initialize conversations for all applications
      const candidateName = candidateSession?.profile?.full_name || 'Candidate';
      data.forEach(app => {
        const companyName = app.company || 'Company';
        initializeConversation(app.id, candidateName, app.job_title, companyName);
      });
      
      // Select first application by default
      if (data.length > 0 && !selectedApplication) {
        setSelectedApplication(data[0]);
        loadJobDetails(data[0].job_id);
      }
    } catch (error) {
      console.error('Failed to load applications:', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const loadJobDetails = async (jobId) => {
    try {
      const job = await getJobDetails(jobId);
      setJobDetails(job);
    } catch (error) {
      console.error('Failed to load job details:', error);
    }
  };

  const handleApplicationSelect = (application) => {
    setSelectedApplication(application);
    loadJobDetails(application.job_id);
  };

  const formatInterviewSlot = (details) => {
    if (!details) return 'Awaiting schedule';
    if (details.date && details.start_time) {
      return `${details.date} at ${details.start_time}${details.timezone ? ` (${details.timezone})` : ''}`;
    }
    if (details.scheduled_at) {
      return new Date(details.scheduled_at).toLocaleString();
    }
    return 'Awaiting schedule';
  };

  const buildCandidateNotifications = (apps) => {
    return apps
      .filter((app) => Boolean(app.interview_details) && (app.status === 'interview' || app.status === 'review'))
      .map((app) => {
        const slot = formatInterviewSlot(app.interview_details);
        return {
          id: `${app.id}-interview`,
          title: 'Interview Scheduled',
          message: `${app.job_title} · ${slot}`,
          meetingLink: app.interview_details?.meeting_link,
          status: app.status,
        };
      });
  };

  const loadChatMessages = async () => {
    if (!selectedApplication) return;
    const messages = await getMessages(selectedApplication.id);
    setChatMessages(messages);

    // Calculate unread count (messages from HR that candidate hasn't read)
    const unread = await getUnreadCount(selectedApplication.id, 'Candidate');
    setUnreadCount(unread);
  };

  // Update unread count whenever applications or selected application changes
  useEffect(() => {
    if (selectedApplication) {
      getUnreadCount(selectedApplication.id, 'Candidate').then(setUnreadCount);
    }
  }, [applications, selectedApplication]);

  const handleSendMessage = async () => {
    if (!chatInput.trim() || !selectedApplication) return;

    const candidateSession = SessionManager.getSession('candidate');
    const candidateName = candidateSession?.profile?.full_name || 'Candidate';
    await sendMessage(selectedApplication.id, 'Candidate', chatInput, candidateName);
    setChatInput('');
    await loadChatMessages();
  };

  const handleOpenChat = async () => {
    setChatModalOpen(true);
    await loadChatMessages();

    if (selectedApplication) {
      await markMessagesAsRead(selectedApplication.id, 'HR');
      setUnreadCount(0);
    }
  };

  // Load messages when selected application changes
  useEffect(() => {
    if (selectedApplication) {
      loadChatMessages();
    }
  }, [selectedApplication]);

  const handleCopyMeetingLink = async (link) => {
    if (!link) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = link;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      alert('Meeting link copied to clipboard');
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const getStatusSteps = (status) => {
    const steps = [
      { key: 'applied', label: 'Applied', date: null },
      { key: 'review', label: 'Under Review', date: null },
      { key: 'interview', label: 'Interview', date: null },
      { key: 'offer', label: 'Offer', date: null },
    ];
    
    const statusOrder = ['applied', 'review', 'interview', 'offer'];
    let currentIndex = statusOrder.indexOf(status);
    
    // If status is rejected, show progress up to review stage
    if (status === 'rejected') {
      currentIndex = 1; // Show progress up to review
    }
    
    // If status not found, default to applied
    if (currentIndex === -1) {
      currentIndex = 0;
    }
    
    // Set dates for completed steps
    if (selectedApplication?.applied_at) {
      const appliedDate = new Date(selectedApplication.applied_at);
      const dateFormat = { day: 'numeric', month: 'short', year: '2-digit' };
      
      // Applied date is always shown
      steps[0].date = appliedDate.toLocaleDateString('en-GB', dateFormat);
      
      // If status is review or beyond, show review date (use applied date as placeholder)
      if (currentIndex >= 1) {
        steps[1].date = appliedDate.toLocaleDateString('en-GB', dateFormat);
      }
      
      // If status is interview or beyond, show interview date
      if (currentIndex >= 2) {
        steps[2].date = appliedDate.toLocaleDateString('en-GB', dateFormat);
      }
      
      // If status is offer, show offer date
      if (currentIndex >= 3) {
        steps[3].date = appliedDate.toLocaleDateString('en-GB', dateFormat);
      }
    }
    
    return { steps, currentIndex };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'applied':
        return 'default';
      case 'review':
        return 'default';
      case 'interview':
        return 'default';
      case 'offer':
        return 'default';
      case 'rejected':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'applied':
        return 'Application Sent';
      case 'review':
        return 'Under Review';
      case 'interview':
        return 'Interview Scheduled';
      case 'offer':
        return 'Offer Received';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };

  const selectedInterviewDetails = selectedApplication?.interview_details;

  if (loading) {
    return (
      <div className="relative flex min-h-screen flex-col bg-background-light dark:bg-background-dark">
        <Header />
        <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-20">
              <p className="text-gray-600 dark:text-gray-400">Loading applications...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-[#f5f7fb] dark:bg-background-dark">
      <Header />
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Job Application Status
              </h1>
              {/* Chat Icon with Notification Badge */}
              <button
                onClick={handleOpenChat}
                className="relative p-3 rounded-full bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-shadow border border-gray-200 dark:border-gray-700"
                title="Messages from HR"
                disabled={!selectedApplication}
              >
                <Icon name="chat" size={24} className="text-primary" />
                {/* Notification Badge - shows unread message count */}
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white animate-pulse">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {applications.length}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                  Total applies
                </span>
              </div>
              <div>
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {applications.filter(app => app.status === 'review' || app.status === 'interview' || app.status === 'offer').length}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                  Active applications
                </span>
              </div>
            </div>
          </div>

          {candidateNotifications.length > 0 && (
            <div className="mb-6 space-y-3">
              {candidateNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm dark:border-indigo-900/40 dark:bg-gray-900"
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-full bg-indigo-100 p-2 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-300">
                      <Icon name="event_available" size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {notification.message}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {notification.meetingLink && (
                      <>
                        <a
                          href={notification.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-primary hover:underline"
                        >
                          Join Call
                        </a>
                        <button
                          type="button"
                          onClick={() => handleCopyMeetingLink(notification.meetingLink)}
                          className="text-xs font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                        >
                          Copy Link
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Application List */}
            <div className="lg:col-span-1">
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex gap-2">
                    <button className="px-4 py-2 text-sm font-medium rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                      My Applications ({applications.length})
                    </button>
                  </div>
                </div>
                
                <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[calc(100vh-300px)] overflow-y-auto">
                  {applications.length === 0 ? (
                    <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                      <p className="mb-2">No applications yet</p>
                      <button
                        onClick={() => navigate('/jobs')}
                        className="text-primary hover:underline text-sm"
                      >
                        Browse Jobs
                      </button>
                    </div>
                  ) : (
                    applications.map((app) => (
                      <div
                        key={app.id}
                        onClick={() => handleApplicationSelect(app)}
                        className={`p-4 cursor-pointer transition-colors ${
                          selectedApplication?.id === app.id
                            ? 'bg-primary/5 border-l-4 border-primary'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                              {app.job_title}
                            </h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                              {app.company}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                              <Icon name="check_circle" size={14} className="text-green-500" />
                              <span>Application sent {formatDate(app.applied_at)}</span>
                            </div>
                          </div>
                          <Avatar
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(app.company)}&background=6366f1&color=fff`}
                            alt={app.company}
                            size={10}
                            className="rounded-lg flex-shrink-0"
                          />
                        </div>
                        <div className="mt-2">
                          <Badge variant={getStatusBadgeVariant(app.status)}>
                            {getStatusLabel(app.status)}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel: Application Details */}
            <div className="lg:col-span-2">
              {selectedApplication ? (
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  {/* Job Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {selectedApplication.job_title}
                      </h2>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {selectedApplication.company}
                        </span>
                        <Badge variant={getStatusBadgeVariant(selectedApplication.status)}>
                          {getStatusLabel(selectedApplication.status)}
                        </Badge>
                      </div>
                      <a
                        href={`/jobs/${selectedApplication.job_id}`}
                        className="text-sm text-primary hover:underline"
                      >
                        View job details
                      </a>
                    </div>
                    <Avatar
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(selectedApplication.company)}&background=6366f1&color=fff`}
                      alt={selectedApplication.company}
                      size={16}
                      className="rounded-lg flex-shrink-0"
                    />
                  </div>

                  {/* Application Status Progress */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Application Status
                    </h3>
                    {selectedApplication.status === 'rejected' ? (
                      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                          <Icon name="close" size={20} />
                          <span className="font-semibold">Application Rejected</span>
                        </div>
                        <p className="text-sm text-red-600 dark:text-red-300 mt-2">
                          Unfortunately, your application was not selected for this position.
                        </p>
                      </div>
                    ) : (
                      <div className="relative">
                        {/* Progress Line */}
                        <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700">
                          <div
                            className={`h-full transition-all duration-300 ${
                              selectedApplication.status === 'rejected' 
                                ? 'bg-red-500' 
                                : 'bg-primary'
                            }`}
                            style={{
                              width: `${((getStatusSteps(selectedApplication.status).currentIndex + 1) / getStatusSteps(selectedApplication.status).steps.length) * 100}%`,
                            }}
                          />
                        </div>

                        {/* Steps */}
                        <div className="relative flex justify-between">
                          {getStatusSteps(selectedApplication.status).steps.map((step, index) => {
                            const isCompleted = index <= getStatusSteps(selectedApplication.status).currentIndex;
                            const isCurrent = index === getStatusSteps(selectedApplication.status).currentIndex;
                            
                            return (
                              <div key={step.key} className="flex flex-col items-center" style={{ width: `${100 / getStatusSteps(selectedApplication.status).steps.length}%` }}>
                                <div
                                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                                    isCompleted
                                      ? 'bg-primary border-primary text-white'
                                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400'
                                  }`}
                                >
                                  {isCompleted ? (
                                    <Icon name="check_circle" size={20} />
                                  ) : (
                                    <span className="text-sm font-semibold">{index + 1}</span>
                                  )}
                                </div>
                                <div className="mt-2 text-center">
                                  <p className={`text-xs font-medium ${
                                    isCurrent
                                      ? 'text-primary'
                                      : isCompleted
                                      ? 'text-gray-700 dark:text-gray-300'
                                      : 'text-gray-400 dark:text-gray-500'
                                  }`}>
                                    {step.label}
                                  </p>
                                  {step.date && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      {step.date}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Application Details */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Application Details
                      </h3>
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Applied Date:</span>
                          <span className="text-gray-900 dark:text-white font-medium">
                            {formatDate(selectedApplication.applied_at)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Status:</span>
                          <span className="text-gray-900 dark:text-white font-medium">
                            {getStatusLabel(selectedApplication.status)}
                          </span>
                        </div>
                        {selectedApplication.cover_letter && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Cover Letter:</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                              {selectedApplication.cover_letter}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedInterviewDetails && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Interview Schedule
                        </h3>
                        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 space-y-2 border border-indigo-100 dark:border-indigo-900/40">
                          <div className="flex items-center gap-2 text-sm text-indigo-900 dark:text-indigo-200">
                            <Icon name="event" size={18} />
                            <span>{formatInterviewSlot(selectedInterviewDetails)}</span>
                          </div>
                          {selectedInterviewDetails.location && (
                            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <Icon name="place" size={18} />
                              <span>{selectedInterviewDetails.location}</span>
                            </div>
                          )}
                          {selectedInterviewDetails.panel && selectedInterviewDetails.panel.length > 0 && (
                            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <Icon name="group" size={18} />
                              <span>Panel: {selectedInterviewDetails.panel.join(', ')}</span>
                            </div>
                          )}
                          {selectedInterviewDetails.notes && (
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              Notes: {selectedInterviewDetails.notes}
                            </p>
                          )}
                          {selectedInterviewDetails.meeting_link && (
                            <div className="flex flex-wrap gap-3 pt-2">
                              <a
                                href={selectedInterviewDetails.meeting_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white"
                              >
                                <Icon name="videocam" size={16} />
                                Join Meeting
                              </a>
                              <button
                                type="button"
                                onClick={() => handleCopyMeetingLink(selectedInterviewDetails.meeting_link)}
                                className="inline-flex items-center gap-2 text-xs font-semibold text-primary"
                              >
                                <Icon name="content_copy" size={14} />
                                Copy Link
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {jobDetails && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Job Information
                        </h3>
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Location:</span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              {jobDetails.location || '—'}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Job Type:</span>
                            <span className="text-gray-900 dark:text-white font-medium">
                              {jobDetails.type || '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                  <Icon name="work" size={48} className="text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Select an application to view details
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Chat Modal */}
      {chatModalOpen && (
        <div 
          id="candidate-chat-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setChatModalOpen(false)}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 h-[600px] flex flex-col border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="business" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">
                    {selectedApplication?.company || 'Company'} HR Team
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedApplication?.job_title || 'Recruitment Team'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setChatModalOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <Icon name="close" size={24} className="text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {chatMessages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'Candidate' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                      message.sender === 'Candidate'
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                    }`}
                  >
                    <p className="text-sm">{message.text}</p>
                    <p className={`text-xs mt-1 ${message.sender === 'Candidate' ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex gap-2 mb-3">
                <button
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => {
                    setChatInput('Thank you for the update! I am available for an interview.');
                  }}
                >
                  <Icon name="check_circle" size={14} />
                  Confirm Availability
                </button>
                <label className="cursor-pointer">
                  <button
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    as="span"
                  >
                    <Icon name="attach_file" size={14} />
                    Attach Document
                  </button>
                  <input 
                    type="file" 
                    className="hidden" 
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        alert(`File "${e.target.files[0].name}" attached (mock)`);
                      }
                    }} 
                  />
                </label>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Type your message..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSendMessage();
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!chatInput.trim()}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Icon name="send" size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyApplicationsPage;

